import { Box, Flex, Spinner } from '@chakra-ui/react';
import { useDebouncedCallback } from 'hooks/useDebounce';
import { useWindowSize } from 'hooks/useWindowSize';
import clonedeep from 'lodash.clonedeep';
import E2ClientProvider from 'modules/E2ClientProvider/E2ClientProvider';
import { store, useAppSelector } from 'modules/Editor/store';
import {
  selectInfographHeightPx,
  selectInfographWidthPx,
  selectPageBackgroundString,
  selectPhishing,
} from 'modules/Editor/store/infographSelector';
import { updateWidgetNoHistory } from 'modules/Editor/store/infographSlice';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import NotFound from 'modules/NotFound';
import { useEffect, useRef, useState } from 'react';
import { Provider } from 'react-redux';
import { PageId } from 'types/idTypes';
import { InfographState } from 'types/infographTypes';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import { WidgetRenderer } from 'widgets/WidgetRenderer';
import { PreviewControlProvider } from 'widgets/store/PreviewControlProvider';

import { InfographLoader } from '../InfographLoader';
import { MIN_RESPONSIVE_LAYOUT_SCREEN_WIDTH, MIN_SCREEN_WIDTH } from './ResponsiveLinkView.config';
import { ResponsivePageViewProps } from './ResponsiveLinkView.types';
import { LegalNote } from './components/LegalNote';
import { ResponsiveLinkHead } from './components/ResponsiveLinkHead';
import { responsiveLayout } from './responsiveLayout';
import { resetAllWidgets, scaleDecorativeWidgets, scaleGroups } from './scaleWidgets';
import { segmentAndGroupWidgets } from './segmentAndGroupWidgets';

const ResponsiveLinkViewPage = () => {
  const windowSize = useWindowSize();
  const hasPageOverflowHidden = windowSize.width >= MIN_RESPONSIVE_LAYOUT_SCREEN_WIDTH;

  const isPhishing = useAppSelector(selectPhishing);

  if (isPhishing) {
    return <NotFound />;
  }

  return (
    <ResponsivePageView
      firstLoadDebounce={0}
      backgroundElement={document.body}
      pageContainerOverflow={hasPageOverflowHidden ? 'hidden' : ''}
      windowSize={windowSize}
    />
  );
};

export const ResponsivePageView = ({
  backgroundElement,
  pageContainerOverflow,
  windowSize,
  firstLoadDebounce = 200,
  minPageHeight,
  pageHeight,
  customDistanceLeft,
  customShouldResize,
}: ResponsivePageViewProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const widthPx = useAppSelector(selectInfographWidthPx);
  const heightPx = useAppSelector(selectInfographHeightPx);
  const activePageId = useAppSelector(selectActivePage);
  const pageBackground = useAppSelector(selectPageBackgroundString(activePageId));

  // stores the original unmodified infograph state (responsive will change widget size/position)
  const originalInfographRef = useRef<InfographState | null>(null);
  const designContainerElRef = useRef<HTMLDivElement | null>(null);
  const initialContainerHeightRef = useRef<number | null>(null);
  const hasInitiallyLoaded = useRef(false);

  // assign the original infograph state if it hasn't been assigned yet
  if (activePageId && !originalInfographRef.current) {
    originalInfographRef.current = clonedeep(store.getState().infograph);
  }

  // resizes and positions the widgets to fit the provided window size
  const onResponsiveResize = (windowSize: { width: number; height: number }, activePageId: PageId) => {
    if (!originalInfographRef.current) return;
    if (designContainerElRef.current === null) return;

    if (initialContainerHeightRef.current === null) {
      initialContainerHeightRef.current = designContainerElRef.current.clientHeight;
    }

    const infograph = originalInfographRef.current;
    const widgets = infograph.widgets;
    const designContainerEl = designContainerElRef.current;
    const ws = { width: Math.max(MIN_SCREEN_WIDTH, windowSize.width), height: windowSize.height };

    // first reset all widgets to their original state (pre-responsive scaling/positioning)
    const updateWidgets = resetAllWidgets(widgets);
    store.dispatch(updateWidgetNoHistory(updateWidgets));

    // identify groups of widgets
    const { decorativeWidgets, baseLayoutWidgetGroups, adjacentWidgetGroups } = segmentAndGroupWidgets(
      infograph,
      activePageId,
    );

    const distanceToScreenLeft = customDistanceLeft
      ? customDistanceLeft(designContainerEl)
      : designContainerEl?.getBoundingClientRect().left || 0;
    const groups = [...baseLayoutWidgetGroups, ...adjacentWidgetGroups];

    // update the store with the new widget positions/sizes
    const newDecorative = scaleDecorativeWidgets(
      { width: ws.width, height: Math.max(ws.height, infograph.size.heightPx) },
      decorativeWidgets,
      widgets,
      distanceToScreenLeft,
    );
    store.dispatch(updateWidgetNoHistory(newDecorative));
    const newGroups = scaleGroups(ws, groups, infograph, distanceToScreenLeft);
    store.dispatch(updateWidgetNoHistory(newGroups));

    const responsive = responsiveLayout(infograph, store.getState().infograph, groups, ws);

    designContainerElRef.current!.style.height = initialContainerHeightRef.current + 'px';
    if (!responsive) return;

    // dont handle smaller screens than this
    if (ws.width < MIN_RESPONSIVE_LAYOUT_SCREEN_WIDTH) {
      store.dispatch(updateWidgetNoHistory(responsive.updateWidgetsArr));
    }

    // the total height can change when content is stacked - so update the container height
    if (
      typeof responsive.totalHeight === 'number' &&
      responsive.totalHeight > windowSize.height &&
      windowSize.width < MIN_RESPONSIVE_LAYOUT_SCREEN_WIDTH
    ) {
      designContainerElRef.current!.style.height = responsive.totalHeight + 'px';
      const newDecorative = scaleDecorativeWidgets(
        { width: ws.width, height: responsive.totalHeight },
        decorativeWidgets,
        widgets,
        distanceToScreenLeft,
      );

      store.dispatch(updateWidgetNoHistory(newDecorative));
    }
  };

  const debouncedResponsiveRezise = useDebouncedCallback(
    () => {
      if (!activePageId) return;
      if (!hasInitiallyLoaded.current) hasInitiallyLoaded.current = true;

      if (customShouldResize && !customShouldResize()) {
        setIsLoading(false);
        return;
      }
      onResponsiveResize(windowSize, activePageId);
      setIsLoading(false);
    },
    hasInitiallyLoaded.current ? 200 : firstLoadDebounce,
  );

  useEffect(debouncedResponsiveRezise, [windowSize.width, activePageId, windowSize.height, debouncedResponsiveRezise]);

  useEffect(() => setIsLoading(true), [windowSize.width]);

  if (backgroundElement) backgroundElement.style.background = pageBackground;

  const isWidthLargerThanScreen = widthPx > windowSize.width;
  const isHeightLargerThanScreen =
    heightPx > windowSize.height && heightPx > parseFloat(designContainerElRef.current?.style.height || '0');

  return (
    <Flex
      display='flex'
      justify={isWidthLargerThanScreen ? 'left' : 'center'}
      overflow={pageContainerOverflow}
      minHeight={minPageHeight || '100vh'}
      height={pageHeight || isHeightLargerThanScreen ? heightPx + 'px' : undefined}
    >
      <Box
        ref={designContainerElRef}
        width={isWidthLargerThanScreen ? MIN_SCREEN_WIDTH + 'px' : widthPx + 'px'}
        position='relative'
      >
        {isLoading ? (
          <Flex
            position='absolute'
            left={(isWidthLargerThanScreen ? windowSize.width : widthPx) / 2 + 'px'}
            minH={windowSize.height + 'px'}
            align='center'
            justify='center'
          >
            <Spinner />
          </Flex>
        ) : (
          <WidgetRenderer pageId={activePageId} isReadOnly renderType={ReadOnlyRenderType.StructureTreeList} />
        )}
      </Box>
    </Flex>
  );
};

const ResponsiveLinkView = () => {
  return (
    <Provider store={store}>
      <E2ClientProvider>
        <InfographLoader isEditor={false}>
          <ResponsiveLinkHead />
          <PreviewControlProvider enableChartAnimation>
            <ResponsiveLinkViewPage />
          </PreviewControlProvider>
          <LegalNote />
        </InfographLoader>
      </E2ClientProvider>
    </Provider>
  );
};

export default ResponsiveLinkView;
